import { faAdd } from "@fortawesome/free-solid-svg-icons/faAdd";
import { faArrowsTurnToDots } from "@fortawesome/free-solid-svg-icons/faArrowsTurnToDots";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faListCheck } from "@fortawesome/free-solid-svg-icons/faListCheck";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { Tooltip } from "../../../utils/local-antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { FontSize, Text } from "../../../components/Text";
import { SortableChild } from "../../../components/dnd/SortableChild";
import i18n from "../../../i18n";
import { KanbanCard as KanbanCardModel } from "../../../model/kanban/KanbanCard";
import { KanbanColumn } from "../../../model/kanban/KanbanColumn";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { standardDate } from "../../../utils/Date";
import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { useDrawersContext } from "../../tasks/drawers-context";
import { TaskKind } from "../../../model/task/shared/TasksKind.enum";
import { EntityIcons } from "../../../utils/GlobalIcons";
import { Shadow } from "../../../theme/Shadow";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { TestIDs } from "../../../tests/types/testIds";
import { getCurrencySymbol } from "../../../utils/pricingUtils";

export const StyledKanbanCard = styled(Flex)<{
  isParentDefault: boolean;
  $isClosed?: boolean;
}>`
  box-sizing: border-box;
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.s};

  background-color: ${({ theme }) => theme.white};
  border-radius: ${Rounded.lg};
  border-bottom-right-radius: ${({ $isClosed }) =>
    $isClosed ? "0" : Rounded.lg};
  border-bottom-left-radius: ${({ $isClosed }) =>
    $isClosed ? "0" : Rounded.lg};
  width: 100%;

  cursor: ${({ isParentDefault }) => (isParentDefault ? "auto" : "grab")};
  direction: ${i18n.language === AppLangs.AR ? "rtl" : "ltr"};

  border: 1px solid ${({ theme }) => theme.darkSub};
  box-shadow: ${Shadow.SM};
  transition:
    box-shadow 0.2s,
    border-color 0.2s;

  .kanban-card-actions {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
  }

  &:hover {
    border-color: ${({ theme }) => theme.lightergray};
  }

  &:hover .kanban-card-actions {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    .kanban-card-actions {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

export const KanbanCardActionsWrapper = styled(Flex)`
  gap: ${Spacing.s};
  align-items: flex-start;
`;

export const KnabanCardName = styled(Text)`
  font-size: ${FontSize.m};
  font-weight: 700;
  line-height: normal;
`;

export const KanbanCardActionButton = styled(Button)`
  padding: ${Spacing.xs};
  background-color: ${(props) => props.theme.transparent};
  margin: 0;
  color: ${(props) => props.theme.main};
`;

const ClosedByContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  flex-direction: column;
  padding: ${Spacing.xs} ${Spacing.s};
  background-color: ${({ theme }) => theme.lightSub};
  border-top: 1px solid ${({ theme }) => theme.darkSub};
  border-bottom-left-radius: ${Rounded.lg};
  border-bottom-right-radius: ${Rounded.lg};
  width: 100%;
  direction: ${i18n.language === AppLangs.AR ? "rtl" : "ltr"};
`;

const StyledSortable = styled(SortableChild)`
  width: 100%;
`;

type KanbanCardProps = {
  card: KanbanCardModel;
  column?: KanbanColumn;
};

export const KanbanCard: React.FC<KanbanCardProps> = ({ card, column }) => {
  const { t } = useTranslation();

  const {
    setKanbanCard,
    setKanbanCardReadVisible,
    setKanbanCardUpdateVisible,
    setTasksReadVisible,
    setTaskCreateVisible,
    setTaskKind,
    setKanbanCardMoveToOtherBoardVisible,
    setKanbanCardDeleteVisible,
    setEntry,
    setEntryDetailsVisible,
  } = useDrawersContext();

  const userPermissions = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.permissions.permissions;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const canCreateTask = userPermissions?.KANBAN_TASK.CREATE;
  const cardFinalized = Boolean(card.closedAt);

  const openCreateTask = () => {
    setTaskKind(TaskKind.KANBAN);
    setTaskCreateVisible(true);
  };

  const onCardClick = (fn: ATVoidFunction<boolean> | VoidFunction) => {
    // The board fetches cards through an aggregation that never attaches a
    // `column` field to the card itself - it only exists as the sibling prop
    // this component was handed. Move-between-kanbans reads `card.column.parentId`
    // to know which board the card is coming *from*, so without this merge it
    // silently sends no source kanban and the API rejects the request.
    setKanbanCard({ ...card, column });
    if (!fn.length) {
      (fn as VoidFunction)();
      return;
    }
    fn(true);
  };

  return (
    <StyledSortable
      id={card._id}
      key={card._id}
      disabled={
        column?.default === true || userPermissions?.KANBAN_CARD.UPDATE !== true
      }
    >
      <StyledKanbanCard
        isParentDefault={column?.default === true}
        $isClosed={Boolean(card.closedAt)}
        dataTestId={TestIDs.kanbans.card.wrapper(card._id)}
      >
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          gap="s"
          width={"100%"}
        >
          <Flex alignItems="center" justifyContent="space-between" gap="s">
            <Flex width={"70%"}>
              <KnabanCardName cutText>{card.title}</KnabanCardName>
            </Flex>

            <AssigneeBanner
              assignees={members?.filter((member) =>
                card?.entry?.assigneeIds?.includes(member._id),
              )}
            />
          </Flex>

          <Text cutText>
            {card.entry?.fields?.name || card.entry?.fields?.email}
          </Text>

          <Text cutText>
            {card.amount || 0}
            {getCurrencySymbol(
              card.entry?.fields?.currency || user?.settings?.currency || "USD",
            )}
          </Text>

          <Flex justifyContent="space-between">
            <KanbanCardActionsWrapper className="kanban-card-actions">
              {card?.entry ? (
                <Tooltip title={t("kanban.card.viewRelatedEntry")}>
                  <KanbanCardActionButton
                    onClick={() =>
                      onCardClick(() => {
                        setEntry(card.entry!);
                        setEntryDetailsVisible(true);
                      })
                    }
                    icon={EntityIcons.Entries}
                    fontSize="m"
                  />
                </Tooltip>
              ) : null}
              {canCreateTask && !cardFinalized ? (
                <Tooltip title={t("common.task_create")}>
                  <KanbanCardActionButton
                    onClick={() => onCardClick(openCreateTask)}
                    icon={faAdd}
                    fontSize="m"
                  />
                </Tooltip>
              ) : null}

              {userPermissions?.KANBAN_TASK.READ ? (
                <Tooltip title={t("tasks.read.drawer.title")}>
                  <KanbanCardActionButton
                    onClick={() => onCardClick(setTasksReadVisible)}
                    icon={faListCheck}
                    fontSize="m"
                  />
                </Tooltip>
              ) : null}

              {userPermissions?.KANBAN_CARD.READ ? (
                <Tooltip title={t("common.preview")}>
                  <KanbanCardActionButton
                    onClick={() => onCardClick(setKanbanCardReadVisible)}
                    icon={faEye}
                    fontSize="m"
                  />
                </Tooltip>
              ) : null}

              {userPermissions?.KANBAN_CARD.UPDATE && !cardFinalized ? (
                <Tooltip title={t("common.edit")}>
                  <KanbanCardActionButton
                    dataTestId={TestIDs.kanbans.card.edit.button(card._id)}
                    onClick={() => onCardClick(setKanbanCardUpdateVisible)}
                    icon={faPenToSquare}
                    fontSize="m"
                  />
                </Tooltip>
              ) : null}

              {userPermissions?.KANBAN_CARD.UPDATE &&
              kanbansMeta?.length > 1 &&
              !cardFinalized ? (
                <Tooltip title={t("kanban.card.moveBetweenKanbans")}>
                  <KanbanCardActionButton
                    onClick={() =>
                      onCardClick(setKanbanCardMoveToOtherBoardVisible)
                    }
                    icon={faArrowsTurnToDots}
                    fontSize="m"
                  />
                </Tooltip>
              ) : null}

              {userPermissions?.KANBAN_CARD.DELETE ? (
                <Tooltip title={t("kanban.card.delete")}>
                  <KanbanCardActionButton
                    dataTestId={TestIDs.kanbans.card.delete.button(card._id)}
                    onClick={() => onCardClick(setKanbanCardDeleteVisible)}
                    icon={faTrash}
                    fontSize="m"
                  />
                </Tooltip>
              ) : null}
            </KanbanCardActionsWrapper>
          </Flex>
        </Flex>

        {card.closingDate ? (
          <Flex justifyContent="flex-end">
            <Text fontSize="s" color="gray">
              {t("common.toCloseAt")} {""}
              {standardDate<string>(card.closingDate, user, true)}
            </Text>
          </Flex>
        ) : null}
      </StyledKanbanCard>

      {card.closedByUser ? (
        <ClosedByContainer>
          <Flex alignItems="center" gap="s">
            <Text fontSize="xs">
              {t("common.closedBy", {
                name: card.closedByUser.name,
                date: standardDate<string>(card.closedAt!, user, true),
              })}
            </Text>
            <AssigneeBanner hideSelf assignees={[card.closedByUser]} />
          </Flex>
        </ClosedByContainer>
      ) : null}
    </StyledSortable>
  );
};
